/**
 * AdsSidebar — Carrossel lateral estilo Stories para propagandas na TV.
 *
 * Orquestra o ciclo de reprodução delegando cada responsabilidade a um módulo:
 *   useAdsTimer        → rAF, troca de src via ref, progresso, avanço de slide
 *   MediaContent       → elementos <img>/<video> estáveis (sem remontagem)
 *   StoriesProgressBar → tracinhos de progresso sincronizados com o rAF
 *
 * O overlay de rodapé (título + preço) vive aqui por ser específico do layout
 * da sidebar e não ter lógica de estado própria.
 */

import { useState, useEffect, useRef } from 'react';
import type { Midia } from '@/shared/types';
import { useAdsTimer }       from '../hooks/useAdsTimer';
import MediaContent          from './MediaContent';
import StoriesProgressBar    from './StoriesProgressBar';

interface Props {
  midias:           Midia[];
  blobMap:          ReadonlyMap<string, string>;
  onCicloCompleto?: () => void;
}

export default function AdsSidebar({ midias, blobMap, onCicloCompleto }: Props) {
  const [idx, setIdx] = useState(0);

  // Refs estáveis passados tanto para useAdsTimer (escrita de src) quanto
  // para MediaContent (renderização dos nós DOM) — sem criar dois conjuntos de nós.
  const imgRef   = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reinicia o carrossel quando o Player injeta um novo bloco de 3 mídias
  useEffect(() => { setIdx(0); }, [midias]);

  const midia   = midias[idx] ?? null;
  const isVideo = midia?.tipo === 'video';

  const { progress, handleLoadedMetadata } = useAdsTimer({
    midias,
    idx,
    blobMap,
    videoRef,
    imgRef,
    onNext:          (next) => setIdx(next),
    onCicloCompleto,
  });

  if (midias.length === 0) {
    return <div className="h-full bg-black border-l border-white/[0.06]" />;
  }

  return (
    <div className="h-full relative overflow-hidden bg-black border-l border-white/[0.06]">

      <MediaContent
        midia={midia}
        isVideo={isVideo}
        videoRef={videoRef}
        imgRef={imgRef}
        onLoadedMetadata={handleLoadedMetadata}
      />

      <StoriesProgressBar
        total={midias.length}
        currentIdx={idx}
        progress={progress}
      />

      {/* Overlay de rodapé — gradiente + título/preço em unidades vw para proporcionalidade */}
      {midia && (midia.titulo || midia.preco !== null) && (
        <div
          className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
            padding: '3vw 1.2vw 1.2vw',
          }}
        >
          {midia.titulo && (
            <p
              className="text-white font-bold leading-snug line-clamp-2"
              style={{ fontSize: '1.2vw' }}
            >
              {midia.titulo}
            </p>
          )}
          {midia.preco !== null && (
            <p
              className="text-orange-400 font-black tabular-nums mt-1"
              style={{ fontSize: '2vw' }}
            >
              R$ {midia.preco.toFixed(2).replace('.', ',')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
